import React, {
  ReactElement,
  useState,
  CSSProperties,
  MouseEvent,
  forwardRef,
  Ref,
  FC,
} from "react";

import classnames from "classnames";

import { StyledTooltip } from "./Styles";
import { Portal, useLayerContext } from "../../helpers";
import { layers } from "../../utils";
import useForkRef from "../../utils/useForkRef";
import { Popper, Placement } from "../Popper";
import { TransitionProps } from "../Transitions";
import { FromElement } from "../Transitions/FromElement";

interface ElementWithRef<T> extends ReactElement {
  ref?: Ref<T>;
}

export interface ITooltipProps {
  /** Children element. */
  children: ElementWithRef<HTMLElement>;
  /** Title element. */
  title: ReactElement | string;
  /** Applies passed styles */
  style?: CSSProperties;
  /** Applies passed classes */
  className?: string;
  /** Open */
  open?: boolean;
  /** Placement */
  placement?: Placement;
  /** Transition component */
  TransitionComponent?: React.FC<TransitionProps>;
  /** Light tooltip */
  isLight?: boolean;
  /** Popper Tooltip style */
  popperTooltipStyle?: CSSProperties;
  /** Popper Tooltip class name*/
  popperTooltipClassName?: string;
  /** Remove tooltip */
  disable?: boolean;
}

interface ITooltipAnimation extends TransitionProps {
  children: ReactElement;
}

const TooltipAnimation: FC<ITooltipAnimation> = ({ children, ...props }) => {
  return (
    <FromElement timeout={0} {...props}>
      {children}
    </FromElement>
  );
};

export const Tooltip = forwardRef<HTMLElement, ITooltipProps>(
  (
    {
      children,
      title,
      className,
      style,
      isLight = false,
      placement = "top",
      TransitionComponent = TooltipAnimation,
      popperTooltipStyle,
      popperTooltipClassName,
      disable,
    },
    ref
  ) => {
    const { zIndex = layers.skyscraper } = useLayerContext();
    const [open, setOpen] = useState(false);
    const [childNode, setChildNode] = useState<HTMLElement | null>();

    const handleEnter = (event: MouseEvent<HTMLElement>): void => {
      const childrenProps = children.props;

      if (event.type === "mouseover" && childrenProps.onMouseOver) {
        childrenProps.onMouseOver(event);
      }

      childNode && childNode.removeAttribute("title");

      setOpen(true);
    };

    const handleLeave = (event: MouseEvent<HTMLElement>): void => {
      const childrenProps = children.props;

      if (
        event.type === "mouseleave" &&
        childrenProps.onMouseLeave &&
        event.currentTarget === childNode
      ) {
        childrenProps.onMouseLeave(event);
      }

      setOpen(false);
    };

    const handleOwnRef = useForkRef(children.ref, ref);
    const handleRef = useForkRef(setChildNode, handleOwnRef);

    const childrenProps = {
      onMouseOver: handleEnter,
      onMouseLeave: handleLeave,
      ref: handleRef,
    };

    return (
      <>
        {React.cloneElement(children, childrenProps)}
        {!disable && (
          <Portal>
            <Popper
              anchorEl={childNode}
              open={childNode ? open : false}
              placement={placement}
              transition
              className={popperTooltipClassName}
              style={{ zIndex, ...popperTooltipStyle }}
              role="tooltip"
            >
              {({ transitionProps = {} }) => (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - due to @node/types making ts more strict
                <TransitionComponent {...transitionProps} style={style}>
                  <StyledTooltip
                    className={classnames("c-tooltip-ds", className)}
                    $isLight={isLight}
                  >
                    {title}
                  </StyledTooltip>
                </TransitionComponent>
              )}
            </Popper>
          </Portal>
        )}
      </>
    );
  }
);

Tooltip.displayName = "Tooltip";
